import React, { useEffect, useRef, useCallback } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TripOrderCard, { ITripOrderCardData } from "../trip-order-card";

interface SortableOrderCardProps {
  order: ITripOrderCardData;
  index: number;
  isSelected?: boolean;
  isEditable?: boolean;
  onRemove?: (orderId: number) => void;
  onSelect?: (orderId: number) => void;
}

const SortableOrderCard: React.FC<SortableOrderCardProps> = ({
  order,
  index,
  isSelected = false,
  isEditable = true,
  onRemove,
  onSelect,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: order.id,
    disabled: !isEditable,
  });

  // Scroll into view when selected
  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isSelected]);

  // Combine refs
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      cardRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef]
  );

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <div ref={setRefs} style={style} {...attributes}>
      <TripOrderCard
        order={order}
        index={index}
        isDragging={isDragging}
        isSelected={isSelected}
        isEditable={isEditable}
        dragHandleProps={listeners}
        onRemove={onRemove}
        onSelect={onSelect}
      />
    </div>
  );
};

export default SortableOrderCard;
